import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminLoadingState,
} from "../components/AdminPageShell";
import StaffPagination from "./components/staff/StaffPagination";
import { useAdminLedger, useAdminResourcePersons } from "../store/admin";
import {
  formatCurrency,
  resourcePersonName,
} from "./components/resourcePersons/resourcePersonUtils";
import { formatDate } from "./components/invoices/invoiceUtils";

// ===== RESOURCE PERSON EARNINGS PAGE — GET /api/admin/resource-persons/:id/earnings =====
const ResourcePersonEarningsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    resourcePersonEarnings,
    resourcePersonEarningsLoading,
    resourcePersonEarningsError,
    fetchResourcePersonEarnings,
  } = useAdminResourcePersons();

  const {
    ledgerEntries,
    ledgerLoading,
    ledgerError,
    ledgerTotal,
    ledgerPages,
    fetchResourcePersonLedger,
  } = useAdminLedger();

  const [ledgerPage, setLedgerPage] = useState(1);

  // GET /api/admin/resource-persons/:id/earnings
  useEffect(() => {
    if (id) {
      fetchResourcePersonEarnings(id);
    }
  }, [id, fetchResourcePersonEarnings]);

  // GET /api/ledger/resource-person/:id
  useEffect(() => {
    if (id) {
      fetchResourcePersonLedger(id, { page: ledgerPage, limit: 10 });
    }
  }, [id, ledgerPage, fetchResourcePersonLedger]);

  const goBack = () => navigate("/admin/resource-persons");

  const earnings = resourcePersonEarnings || {};
  const financial = earnings.financialSummary || {};
  const withdrawals = earnings.withdrawalSummary || {};

  // Financial summary cards — matches the /earnings payload.
  const financialCards = [
    { label: "Cumulative Earnings", value: financial.cumulativeEarnings },
    { label: "Total Withdrawals Paid", value: financial.totalWithdrawalsPaid },
    { label: "Available Balance", value: financial.availableBalance },
    { label: "Pending Withdrawals", value: financial.pendingWithdrawals },
  ];

  // Withdrawal summary cards — counts of withdrawal requests by state.
  const withdrawalCards = [
    { label: "Total Withdrawals", value: withdrawals.totalWithdrawals },
    { label: "Pending", value: withdrawals.pendingWithdrawals },
    { label: "Approved", value: withdrawals.approvedWithdrawals },
    { label: "Rejected", value: withdrawals.rejectedWithdrawals },
  ];

  return (
    <div>
      <AdminNavBar
        title="Resource Person"
        subTitle="Earnings"
        context="View this resource person's earnings and payout summary."
      />
      <AdminPageBody>
        <div className="mb-3">
          <button
            onClick={goBack}
            className="mb-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Resource Persons
          </button>
          <h4 className="mt-2 text-lg font-bold text-slate-900">
            {resourcePersonName(earnings.profile)}
          </h4>
          <span className="text-[13px] text-slate-500">Earnings overview</span>
        </div>

        {/* FINANCIAL SUMMARY */}
        <AdminSectionHeader
          title="Financial Summary"
          description="Aggregated earnings and payout figures for this resource person."
        />

        <AdminTablePanel className="mt-4">
          {resourcePersonEarningsLoading ? (
            <AdminLoadingState />
          ) : resourcePersonEarningsError ? (
            <div className="p-4 text-sm text-red-600">{resourcePersonEarningsError}</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
              {financialCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {card.label}
                  </p>
                  <p className="mb-0 mt-2 text-xl font-bold text-slate-900">
                    {formatCurrency(card.value)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </AdminTablePanel>

        {/* WITHDRAWAL SUMMARY */}
        <div className="mt-6">
          <AdminSectionHeader
            title="Withdrawal Summary"
            description="Breakdown of this resource person's withdrawal requests."
          />
        </div>

        <AdminTablePanel className="mt-4">
          {resourcePersonEarningsLoading ? (
            <AdminLoadingState />
          ) : (
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-4">
              {withdrawalCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <p className="m-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {card.label}
                  </p>
                  <p className="mb-0 mt-2 text-xl font-bold text-slate-900">
                    {card.value ?? 0}
                  </p>
                </div>
              ))}
            </div>
          )}
        </AdminTablePanel>

        {/* LEDGER */}
        <div className="mt-6">
          <AdminSectionHeader
            title="Ledger"
            description="Credits, debits, reserves and withdrawals for this resource person."
            count={`${ledgerTotal} entries`}
          />
        </div>

        <AdminTablePanel className="mt-4">
          {ledgerLoading ? (
            <AdminLoadingState />
          ) : ledgerError ? (
            <div className="p-4 text-sm text-red-600">{ledgerError}</div>
          ) : ledgerEntries.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No ledger entries.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">Type</th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Amount
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Available At
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-slate-100">
                      <td className="px-4 py-2">
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                          {entry.type}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm font-semibold">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {formatDate(entry.availableAt)}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {formatDate(entry.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminTablePanel>

        {ledgerPages > 1 && (
          <div className="mt-4">
            <StaffPagination
              page={ledgerPage}
              lastPage={ledgerPages}
              onPageChange={setLedgerPage}
            />
          </div>
        )}
      </AdminPageBody>
    </div>
  );
};

export default ResourcePersonEarningsPage;